import React, { useMemo } from 'react';
import Typography from '@mui/material/Typography';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CartCounter from '@app/components/CartCounter';
import { ImagesGS } from '@constants/common';
import { computeBillFieldsFromCart } from './editOrderUtils';
import './UpdateOrderDialog.scss';

type EditOrderCartProps = {
    editCart: Record<string, any>;
    order: any;
    onCartChange: (cart: Record<string, any>) => void;
};

const EditOrderCart = ({ editCart, order, onCartChange }: EditOrderCartProps) => {
    const billPreview = useMemo(
        () => computeBillFieldsFromCart(editCart, order),
        [editCart, order]
    );

    const handleValueUpdated = (key: string, val: object, final: number) => {
        if (final === 0) {
            const next = { ...editCart };
            delete next[key];
            onCartChange(next);
        } else {
            onCartChange({ ...editCart, [key]: { ...val, selected: final } });
        }
    };

    const cartEntries = Object.entries(editCart).filter(([, val]) => !!val);
    const itemCount = cartEntries.reduce(
        (sum, [, val]: any) => sum + (val?.selected || 0),
        0
    );

    return (
        <div className="edit-order-cart">
            <div className="edit-order-cart__header">
                <span className="edit-order-cart__title">Bill items</span>
                {itemCount > 0 && (
                    <span className="edit-order-cart__count">{itemCount}</span>
                )}
            </div>

            <div className="edit-order-cart__items">
                {!cartEntries.length && (
                    <div className="edit-order-cart__empty">
                        <ShoppingCartOutlinedIcon className="icon" />
                        <Typography variant="body2" fontWeight={600}>
                            No items in bill
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Tap menu items to add them here
                        </Typography>
                    </div>
                )}
                {cartEntries.map(([key, val]: any) => (
                    <div key={key + '-' + val.name} className="edit-order-cart__item">
                        <div className="edit-order-cart__item-img">
                            <img
                                src={val.image || ImagesGS.itemLightPlaceholder}
                                title={val.name}
                                alt=""
                            />
                        </div>
                        <div className="edit-order-cart__item-body">
                            <p className="edit-order-cart__item-name">
                                {(val.name || '').replaceAll('-', ' ')}
                            </p>
                            <span className="edit-order-cart__item-variant">
                                {val.selectedVariants?.label}
                            </span>
                        </div>
                        <div
                            className="edit-order-cart__item-qty"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <CartCounter
                                initialValue={val.selected}
                                onValueUpdate={(final: number) =>
                                    handleValueUpdated(key, val, final)
                                }
                            />
                        </div>
                        <div className="edit-order-cart__item-price">
                            ₹{val?.selectedVariants?.value * val.selected}
                        </div>
                    </div>
                ))}
            </div>

            <div className="edit-order-cart__summary">
                <div className="edit-order-cart__summary-row">
                    <span>Items cost</span>
                    <span>₹{billPreview.itemsCost.toFixed(2)}</span>
                </div>
                {!!order?.serviceRate && order?.isGstBill && (
                    <div className="edit-order-cart__summary-row">
                        <span>Service ({order.serviceRate}%)</span>
                        <span>₹{billPreview.serviceBill.toFixed(2)}</span>
                    </div>
                )}
                {Object.keys(billPreview.gstData || {}).map((gstVal: string) => {
                    if (!!billPreview.gstData[gstVal]) {
                        return (
                            <div key={gstVal} className="edit-order-cart__summary-row">
                                <span>GST ({gstVal}%)</span>
                                <span>₹{billPreview.gstData[gstVal]}</span>
                            </div>
                        );
                    }
                    return null;
                })}
                {!!order?.discount && (
                    <div className="edit-order-cart__summary-row">
                        <span>Discount</span>
                        <span>- ₹{order.discount}</span>
                    </div>
                )}
                <div className="edit-order-cart__summary-row edit-order-cart__summary-row--total">
                    <span>Total</span>
                    <span>₹{billPreview.totalCost}</span>
                </div>
            </div>
        </div>
    );
};

export default EditOrderCart;
